import { useEffect, useState } from 'react';
import PageHeader from '../../Components/PageHeader/PageHeader';
import { ApiHit } from '../../Api/ApiHit';
import { deleteUser, searchUser } from '../../Constant/Constant';
import { useDispatch, useSelector } from 'react-redux';
import { setUserList } from '../../Redux/Actions/UserAction';
import { setPagination } from '../../Redux/Actions/TableAction';
import AppTable from '../../Components/AppTable/AppTable';
import { setApiJson } from '../../Redux/Actions/ApiAction';
import AddUser from './AddUser';
import ActionButton from '../../Components/AppButton/ActionButton';
import DeleteActionButton from '../../Components/AppButton/DeleteActionButton';
import { PencilIcon } from 'lucide-react';
import CustomModal from '../../Components/CustomModal/CustumModal';
import toast from 'react-hot-toast';
import { hasPermission } from '../../utils';
import { getSelectedAccount, getSelectedBook } from '../../Storage/Storage';

function UserMaster() {
  const dispatch = useDispatch();
  const TableReducer = useSelector((state) => state.TableReducer);
  const UserReducer = useSelector((state) => state.UserReducer);
  const ApiReducer = useSelector((state) => state.ApiReducer);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  // Get user permissions
  const canEditUsers = hasPermission('user', 'write');
  const canReadUsers = hasPermission('user', 'read');
  const canDeleteUsers = hasPermission('user', 'delete');
  const canAddUsers = hasPermission('user', 'write');

  const handleModalClose = () => {
    dispatch(setApiJson({}));
    setSelectedUser(null);
    setIsEditModalOpen(false);
    getUsers();
  };

  useEffect(() => {
    if (canReadUsers) {
      getUsers();
    }
  }, [TableReducer.pagination.page, TableReducer.pagination.limit, canReadUsers]);

  const getUsers = () => {
    if (!canReadUsers) {
      toast.error('You do not have permission to view users');
      return;
    }

    setIsLoading(true);
    const json = {
      page: TableReducer.pagination.page,
      limit: TableReducer.pagination.limit,
      search: {
        accountId: getSelectedAccount()?._id,
        bookId : getSelectedBook()?._id
      }
    };
    ApiHit(json, searchUser).then((res) => {
      console.log("res", res);
      if (res?.statusCode === 200) {
        dispatch(setUserList(res?.data?.docs));
        const paginationData = {
          total: res?.data?.totalDocs || 0,
          page: TableReducer.pagination.page,
          limit: TableReducer.pagination.limit,
        };
        dispatch(setPagination(paginationData));
      }
    }).catch((error) => {
      console.error("Error fetching users:", error);
      toast.error("Error fetching users. Please try again.");
    }).finally(() => {
      setIsLoading(false);
    });
  };

  const baseIndex = (TableReducer.pagination.page - 1) * TableReducer.pagination.limit;

  const handleEdit = (user) => {
    if (!canEditUsers) {
      toast.error('You do not have permission to edit users');
      return;
    }
    setSelectedUser(user);
    dispatch(setApiJson(user));
    setIsEditModalOpen(true);
  }

  const handleDelete = (user) => {
    if (!canDeleteUsers) {
      toast.error('You do not have permission to delete users');
      return;
    }

    ApiHit({ _id: user?._id }, deleteUser).then((res) => {
      if (res?.statusCode === 200) {
        toast.success(res?.message || 'User deleted successfully');
        getUsers(); // Refresh user list
      }
      else {
        toast.error(res?.error || 'Failed to delete user')
      }
    }).catch((error) => {
      console.error("Error deleting user:", error);
      toast.error('Failed to delete user');
    });
  }

  const tableHeaders = [
    {
      title: "S.No",
      key: "serialNumber",
      width: "60px",
      align: "center",
      priority: 1,
      render: (_, row, index) => {
        const serialNumber = baseIndex + index + 1;
        return (
          <div className="text-center">
            <span className="text-sm font-medium text-gray-900">{serialNumber}</span>
          </div>
        );
      }
    },
    {
      title: "Full Name",
      key: "name",
      priority: 1,
      render: (value) => (
        <div className="text-sm text-gray-900">
          {value || 'N/A'}
        </div>
      )
    },
    {
      title: "Email",
      key: "email",
      priority: 1,
      render: (value) => (
        <div className="text-sm text-gray-900">
          {value || 'N/A'}
        </div>
      )
    },
    {
      title: "Phone",
      key: "phone",
      priority: 2,
      render: (value) => (
        <div className="text-sm text-gray-900">
          {value || 'N/A'}
        </div>
      )
    },
    {
      title: "Gender",
      key: "gender",
      priority: 2,
      render: (value) => (
        <div className="text-sm text-gray-900">
          {value || 'N/A'}
        </div>
      )
    },
    {
      title: "Role",
      key: "roleId",
      priority: 2,
      render: (value) => (
        <div className="text-sm text-gray-900">
          {value?.roleName || 'N/A'}
        </div>
      )
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (value, row) => {
        // Check if user has any action permissions
        const hasAnyPermission = canEditUsers || canDeleteUsers;

        if (!hasAnyPermission) {
          return (
            <div className="flex justify-center">
              <span className="text-sm text-gray-400">No actions available</span>
            </div>
          );
        }

        return (
          <div className="flex justify-end md:justify-center space-x-2">
            {canEditUsers && (
              <ActionButton
                onClick={() => handleEdit(row)}
                tooltip="Edit User"
                icon={<PencilIcon className='w-4 h-4' />}
              />
            )}

            {canDeleteUsers && (
              <DeleteActionButton
                onConfirm={() => handleDelete(row)}
                itemName="user"
                tooltip="Delete User"
              />
            )}
          </div>
        );
      }
    }

  ];

  // Filter out Actions column if user has no action permissions
  const hasAnyActionPermission = canEditUsers || canDeleteUsers;
  const filteredHeaders = hasAnyActionPermission
    ? tableHeaders
    : tableHeaders.filter(header => header.key !== 'actions');

  // Show access denied message if user doesn't have read permission
  if (!canReadUsers) {
    return (
      <div>
        <PageHeader
          title={'User Management'}
          description={'View and manage system users and their accounts'}
        />
        <div className="flex justify-center items-center h-64">
          <div className="text-center">
            <h3 className="text-lg font-medium text-red-600 mb-2">Access Denied</h3>
            <p className="text-gray-600">You don't have permission to view users.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div >
      <PageHeader
        title={'User Management'}
        description={'View and manage system users and their accounts'}
      />
      <div className="mt-5">
        <AppTable
          TH={filteredHeaders}
          TD={UserReducer.userList || []}
          emptyMessage="No users found"
          hidePagination={false}
          buttonText={canAddUsers ? "Add User" : undefined}
          modalComap={canAddUsers ? (onClose) => <AddUser onClose={onClose} /> : undefined}
          modalSize={"lg"}
          onModalClose={handleModalClose}
        />
      </div>

      {canEditUsers && (
        <CustomModal
          isOpen={isEditModalOpen}
          onClose={handleModalClose}
          size="lg"
        >
          <AddUser
            isEditMode={true}
            userData={selectedUser}
            onClose={handleModalClose}
          />
        </CustomModal>
      )}
    </div>
  );
}

export default UserMaster;
